import { OrderItem } from '@leid/api-orders'
import { AnalyticsBrowser } from '@segment/analytics-next'

import { ContactFormData } from 'app/components/pages/homepage/ContactSection/components/models'
import { OrderInfo, OrderItemInfo } from 'appTypes/orders'

export const analytics = AnalyticsBrowser.load({
  writeKey: process.env.NEXT_PUBLIC_SEGMENT_KEY ?? '',
})

export const trackContactFormSubmit = async (values: ContactFormData, pathname: string): Promise<void> => {
  await analytics.track('formSubmit', {
    category: 'Contact Us',
    action: `${values.email} - ${values.reason}`,
    label: pathname,
  })
}

export const trackNewsletterFormSubmit = async (pathname: string): Promise<void> => {
  await analytics.track('formSubmit', {
    category: 'Newsletter',
    action: 'Sign Up',
    label: pathname,
  })
}

export const trackReadTestimonial = async (action: string, pathname: string): Promise<void> => {
  await analytics.track('readTestimonial', {
    category: 'Read Testimonial',
    action: action,
    label: pathname,
  })
}

export const trackProductSearch = async (searchTerm: string, pathname: string): Promise<void> => {
  await analytics.track('productSearch', {
    category: 'Search',
    action: `Keyword - ${searchTerm}`,
    label: pathname,
  })
}

export const trackProductByLocationSearch = async (location: string, pathname: string): Promise<void> => {
  await analytics.track('productSearch', {
    category: 'Search',
    action: `Location - ${location}`,
    label: pathname,
  })
}

export const trackProductByManagerSearch = async (manager: string, pathname: string): Promise<void> => {
  await analytics.track('productSearch', {
    category: 'Search',
    action: `Manager - ${manager}`,
    label: pathname,
  })
}

export const trackSuccessfulLogin = async (pathname: string): Promise<void> => {
  await analytics.track('loginSuccessful', {
    category: 'Login',
    action: 'Process validated',
    label: pathname,
  })

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: 'processLogin',
      fms: {
        login: {
          processing: false,
          processed: true,
        },
      },
    })
  }
}

export const trackClickLoginInMenu = async (pathname: string): Promise<void> => {
  await analytics.track('loginClick', {
    category: 'Login in Menu',
    action: 'Click',
    label: pathname,
  })
}

export const trackClickLoginInHeader = async (pathname: string): Promise<void> => {
  await analytics.track('loginClick', {
    category: 'Login in Header',
    action: 'Click in Header',
    label: pathname,
  })
}

export const trackClickAllProducts = async (pathname: string): Promise<void> => {
  await analytics.track('buttonClick', {
    category: 'All Products',
    action: 'Click',
    label: pathname,
  })
}

export const trackClickCategoryOnHomepage = async (categoryName: string, pathname: string): Promise<void> => {
  await analytics.track('categorySelection', {
    category: 'Homepage',
    action: `Click ${categoryName} category`,
    label: pathname,
  })
}

export const trackClickAccessoryButton = async (
  accessory: string,
  quantity: number,
  pathname: string,
): Promise<void> => {
  await analytics.track('buttonClick', {
    category: 'Accessory',
    action: 'Click quantity',
    label: pathname,
    accessory: accessory,
    quantity: quantity,
  })
}

export const trackClickDateButton = async (
  date: {
    day: number
    month: number
    year: number
  },
  pathname: string,
): Promise<void> => {
  await analytics.track('optionSelect', {
    category: 'Date',
    action: `${date.day}-${date.month}-${date.year}`,
    label: pathname,
  })
}

export const trackClickTimeButton = async (
  date: {
    day: number
    month: number
    year: number
    time: string
  },
  pathname: string,
): Promise<void> => {
  await analytics.track('optionSelect', {
    category: 'Time',
    action: `${date.day}-${date.month}-${date.year} ${date.time}`,
    label: pathname,
  })
}

export const trackVideoStart = async (videoTitle: string, pathname: string): Promise<void> => {
  await analytics.track('Video Content Started', {
    title: videoTitle,
    page: pathname,
  })
}

export const trackVideoEnd = async (videoTitle: string, pathname: string): Promise<void> => {
  await analytics.track('Video Content Completed', {
    title: videoTitle,
    page: pathname,
  })
}

export const trackCreateAccount = async (pathname: string): Promise<void> => {
  await analytics.track('formSubmit', {
    category: 'Create Account',
    label: pathname,
  })
}

export const trackAddToCart = async (orderItemInfo: OrderItemInfo, pathname: string): Promise<void> => {
  await analytics.track('Product Added', {
    product_id: orderItemInfo.orderItem.productId,
    order_id: orderItemInfo.orderItem.orderId,
    order_item_id: orderItemInfo.orderItem.id,
    category: '',
    name: orderItemInfo.orderItem.productData?.name ?? '',
    price: orderItemInfo.orderItem.price,
    quantity: orderItemInfo.orderItem.amount,
    url: pathname,
  })
}

export const trackRemoveFromCart = async (orderItem: OrderItem, pathname: string): Promise<void> => {
  await analytics.track('Product Removed', {
    product_id: orderItem.productId,
    order_id: orderItem.orderId,
    order_item_id: orderItem.id,
    category: '',
    name: orderItem.productData?.name ?? '',
    price: orderItem.price,
    quantity: orderItem.amount,
    url: pathname,
  })
}

export const trackCheckoutPayment = async (step: number, paymentOption: string): Promise<void> => {
  await analytics.track('Checkout Step Completed', {
    step,
    payment_method: paymentOption,
  })
}

export const trackCheckoutShipment = async (step: number, option: string): Promise<void> => {
  await analytics.track('Checkout Step Completed', {
    step,
    shipping_method: option,
  })
}

export const trackOrderCompleted = async (
  order: OrderInfo,
  orderPrice: number | '',
  isProlongation: boolean,
  pathname: string,
): Promise<void> => {
  const affiliation = isProlongation ? 'Online Store - Prolongation' : 'Online Store'

  await analytics.track('Order Completed', {
    value: orderPrice ?? 0,
    revenue: orderPrice ?? 0,
    total: orderPrice ?? 0,
    currency: 'NOK',
    order_id: order.order.id,
    transaction_id: order.order.id,
    affiliation,
    products: order.orderItems.map((s) => ({
      product_id: s.orderItem.productId,
      name: s.orderItem.productData?.name ?? '',
      price: s.orderItem.price,
      quantity: s.orderItem.amount,
      location: s.location?.id ?? 0,
      url: pathname,
    })),
  })
}
